import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Typography, 
  Box, 
  Button, 
  Alert, 
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Toolbar,
  Chip,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import { 
  Add as AddIcon, 
  ArrowBack as ArrowBackIcon,
  DirectionsCar as VehicleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as ImportIcon,
  Download as ExportIcon,
  Visibility as ViewIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCurrencySymbol } from '../lib/utils';
import { useVRPData } from '../contexts/VRPDataContext';
import { 
  useVehicles, 
  useAvailableLocationsForVehicles,
  useDeleteVehicle
} from '../hooks/useVehicles';
import type { Vehicle } from '../types/database';
import { useLocations } from '../hooks/useLocations';
import { VehicleForm } from '../components/vehicles/VehicleForm';
import { ErrorBoundary } from '../components/ErrorBoundary';
// import { BulkImportDialog } from '../components/bulk/BulkImportDialog';
// import BulkExportDialog from '../components/bulk/BulkExportDialog';

export const Vehicles: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isBulkExportOpen, setIsBulkExportOpen] = useState(false);
  const [selectedVehicle] = useState<Vehicle | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState<string>('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingVehicle, setViewingVehicle] = useState<Vehicle | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  
  // Get currency symbol from project settings
  const currencySymbol = currentProject?.currency ? getCurrencySymbol(currentProject.currency) : '$';
  const { currentScenario } = null /* TODO: Replace with Toolpad navigation */;
  const { currentDataset } = useVRPData();
  
  // CRITICAL: Load locations first (master data dependency) with scenario/dataset context
  const { 
    isLoading: locationsLoading, 
    error: locationsError 
  } = useLocations(currentProject?.id || 0, currentScenario?.id, { 
    limit: 100,
    dataset_id: currentDataset?.id
  });

  // Get available locations for vehicles (with coordinates) - using context-aware data
  const {
    data: availableLocations,
    isLoading: availableLocationsLoading
  } = useAvailableLocationsForVehicles(currentProject?.id || 0, currentScenario?.id, {
    dataset_id: currentDataset?.id
  });

  // DEPENDENT QUERY: Vehicles load with scenario/dataset context
  const { 
    data: vehicles, 
    isLoading: vehiclesLoading, 
    error: vehiclesError 
  } = useVehicles(currentProject?.id || 0, currentScenario?.id, { 
    dataset_id: currentDataset?.id
  });

  const { mutate: deleteVehicle } = useDeleteVehicle();

  // Debug logging for context state
  useEffect(() => {
    console.log('🚗 [Vehicles] Context state:', {
      currentProject: currentProject?.name || 'none',
      currentScenario: currentScenario?.name || 'none',
      currentDataset: currentDataset?.name || 'none',
      vehiclesCount: vehicles?.length || 0,
      hasContext: !!(currentScenario && currentDataset),
      queryContext: {
        projectId: currentProject?.id,
        scenarioId: currentScenario?.id,
        datasetId: currentDataset?.id
      }
    });
  }, [currentProject, currentScenario, currentDataset, vehicles]);

  const handleOpenForm = () => setIsFormOpen(true);
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingVehicle(null);
  };
  const handleBackToDashboard = () => navigate('/dashboard');

  // Handle successful import - refresh data
  const handleImportSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicles', currentProject?.id] });
    setIsBulkImportOpen(false);
    console.log('🔄 Vehicles page: Refreshing vehicles data after successful import');
  };


  const handleViewVehicle = (vehicle?: Vehicle) => {
    const vehicleToView = vehicle || selectedVehicle;
    if (vehicleToView) {
      setViewingVehicle(vehicleToView);
      setViewDialogOpen(true);
    }
  };

  const handleEditVehicle = (vehicle?: Vehicle) => {
    const vehicleToEdit = vehicle || selectedVehicle;
    if (vehicleToEdit) {
      console.log('Edit vehicle:', vehicleToEdit.id);
      setEditingVehicle(vehicleToEdit);
      setIsFormOpen(true);
    }
  };

  const handleDeleteVehicle = (vehicle?: Vehicle) => {
    const vehicleToDelete = vehicle || selectedVehicle;
    if (vehicleToDelete && currentProject) {
      if (window.confirm(`Are you sure you want to delete vehicle "${vehicleToDelete.description}"?`)) {
        deleteVehicle({ 
          vehicleId: vehicleToDelete.id, 
          projectId: currentProject.id 
        });
      }
    }
  };

  const handleSelectRow = (vehicleId: number) => {
    setSelectedRows(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleSelectAllRows = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && vehicles) {
      setSelectedRows(vehicles.map(vehicle => vehicle.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleBulkAction = async () => {
    console.log('🔥 Vehicles bulk action called:', { bulkAction, selectedRows, currentProject });
    if (!bulkAction || selectedRows.length === 0 || !vehicles || !currentProject) return;
    
    if (bulkAction === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selectedRows.length} vehicle(s)?`)) {
        try {
          // Delete vehicles one by one using the mutation function directly
          for (const vehicleId of selectedRows) {
            await new Promise<void>((resolve, reject) => {
              deleteVehicle(
                { vehicleId, projectId: currentProject.id },
                {
                  onSuccess: () => resolve(),
                  onError: (error) => reject(error)
                }
              );
            });
          }
          
          setSelectedRows([]);
          setBulkAction('');
          console.log('✅ Vehicles bulk delete completed successfully');
        } catch (error) {
          console.error('❌ Vehicles bulk delete failed:', error);
          alert('Some vehicles could not be deleted. Please try again.');
        }
      }
    }
  };

  const getVehicleProfileIcon = (profile: string) => {
    switch (profile) {
      case 'driving': return '🚗';
      case 'cycling': return '🚴';
      case 'foot': return '🚶';
      default: return '🚗';
    }
  };

  const getVehicleProfileColor = (profile: string) => {
    switch (profile) {
      case 'driving': return 'primary';
      case 'cycling': return 'success';
      case 'foot': return 'info';
      default: return 'default';
    }
  };

  // Show project selection message if no current project
  if (!currentProject) {
    return (
      <ProfessionalLayout
        pageTitle="Vehicles"
        pageSubtitle="Please select a project to view vehicles"
        activeTab="Vehicles"
        actionButtons={
          <Button 
            variant="contained"
            onClick={() => navigate('/projects')}
            startIcon={<ArrowBackIcon />}
          >
            Select Project
          </Button>
        }
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>No Project Selected</Typography>
          <Typography variant="body1">Please select a project from the dashboard to view its vehicles.</Typography>
          <Button variant="outlined" onClick={handleBackToDashboard} sx={{ mt: 2 }} startIcon={<ArrowBackIcon />}>
            Back to Dashboard
          </Button>
        </Alert>
      </ProfessionalLayout>
    );
  }

  // Show locations loading (master data dependency)
  if (locationsLoading || availableLocationsLoading) {
    return (
      <ProfessionalLayout
        pageTitle="Vehicles"
        pageSubtitle={`Loading location master data for ${currentProject.name}...`}
        activeTab="Vehicles"
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading location master data for {currentProject.name}...
          </Typography>
        </Box>
      </ProfessionalLayout>
    );
  }

  // Handle locations error (critical dependency)
  if (locationsError) {
    return (
      <ProfessionalLayout
        pageTitle="Vehicles"
        pageSubtitle="Error loading location master data"
        activeTab="Vehicles"
        actionButtons={
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        }
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error Loading Location Master Data</Typography>
          <Typography variant="body1">Vehicles require location master data to function properly.</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {locationsError instanceof Error ? locationsError.message : 'Unknown error occurred'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={handleBackToDashboard} sx={{ mr: 2 }} startIcon={<ArrowBackIcon />}>
              Back to Dashboard
            </Button>
            <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
          </Box>
        </Alert>
      </ProfessionalLayout>
    );
  }

  // Main render - Vehicles with location master integration and context
  const pageSubtitle = (() => {
    if (!currentProject?.name) return 'Manage vehicles';
    
    let subtitle = `Manage vehicles for ${currentProject.name}`;
    if (currentScenario?.name) {
      subtitle += ` • Scenario: ${currentScenario.name}`;
    }
    if (currentDataset?.name) {
      subtitle += ` • Dataset: ${currentDataset.name}`;
    }
    return subtitle;
  })();

  return (
    <ProfessionalLayout
      pageTitle="Vehicles"
      pageSubtitle={pageSubtitle}
      activeTab="Vehicles"
      actionButtons={
        <>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setIsBulkImportOpen(true)}
            sx={{ mr: 1 }}
          >
            Import
          </Button>
          {vehicles && vehicles.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => setIsBulkExportOpen(true)}
              sx={{ mr: 1 }}
            >
              Export
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
          >
            New Vehicle
          </Button>
        </>
      }
    >

      {/* Context Warning Alert */}
      {(!currentScenario || !currentDataset) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Incomplete Context</Typography>
          <Typography variant="body2">
            {!currentScenario && !currentDataset 
              ? 'Please select a scenario and dataset to view vehicle data.'
              : !currentScenario 
              ? 'Please select a scenario to proceed.'
              : 'Please select a dataset to load vehicle data.'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Navigate to: Project → Scenarios → Data Management → Vehicles
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/scenarios')}
              size="small"
              sx={{ mr: 1 }}
            >
              Go to Scenarios
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/data-management')}
              size="small"
            >
              Go to Data Management
            </Button>
          </Box>
        </Alert>
      )}

      {/* Vehicles Loading State */}
      {vehiclesLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>Loading vehicles...</Typography>
        </Box>
      )}

      {/* Vehicles Error State */}
      {vehiclesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Error Loading Vehicles</Typography>
          <Typography variant="body2">
            {vehiclesError instanceof Error ? vehiclesError.message : 'Unknown error occurred'}
          </Typography>
        </Alert>
      )}

      {/* Vehicles List */}
      {vehicles && vehicles.length > 0 && (
        <Box>
          {/* Bulk Actions Toolbar */}
          {selectedRows.length > 0 && (
            <Toolbar sx={{ mb: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {selectedRows.length} vehicle{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
              <FormControl sx={{ minWidth: 120, mr: 2 }}>
                <InputLabel>Action</InputLabel>
                <Select
                  value={bulkAction}
                  label="Action"
                  onChange={(e) => setBulkAction(e.target.value)}
                  size="small"
                >
                  <MenuItem value="delete">Delete</MenuItem>
                </Select>
              </FormControl>
              <Button 
                variant="contained" 
                onClick={handleBulkAction}
                disabled={!bulkAction}
                startIcon={<DeleteOutlineIcon />}
              >
                Apply
              </Button>
            </Toolbar>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < vehicles.length}
                      checked={vehicles.length > 0 && selectedRows.length === vehicles.length}
                      onChange={handleSelectAllRows}
                    />
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Profile</TableCell>
                  <TableCell>Start Location</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Time Window</TableCell>
                  <TableCell>Costs</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.map((vehicle) => {
                  const startLocation = availableLocations?.find(loc => loc.id === vehicle.start_location_id);
                  const endLocation = availableLocations?.find(loc => loc.id === vehicle.end_location_id);
                  
                  return (
                    <TableRow 
                      key={vehicle.id}
                      hover
                      selected={selectedRows.includes(vehicle.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRows.includes(vehicle.id)}
                          onChange={() => handleSelectRow(vehicle.id)}
                        />
                      </TableCell>
                      <TableCell 
                        onClick={() => handleViewVehicle(vehicle)}
                        sx={{ fontWeight: 'medium', color: 'primary.main' }}
                      >
                        <Box display="flex" alignItems="center">
                          <span style={{ marginRight: 8 }}>{getVehicleProfileIcon(vehicle.profile)}</span>
                          {vehicle.description}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={vehicle.profile.charAt(0).toUpperCase() + vehicle.profile.slice(1)}
                          color={getVehicleProfileColor(vehicle.profile) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {startLocation?.name || 'Location not found'}
                        {vehicle.end_location_id && vehicle.end_location_id !== vehicle.start_location_id && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            End: {endLocation?.name || 'Location not found'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {vehicle.capacity.join(' / ')}
                      </TableCell>
                      <TableCell>
                        {Math.floor(vehicle.tw_start / 3600).toString().padStart(2, '0')}:
                        {Math.floor((vehicle.tw_start % 3600) / 60).toString().padStart(2, '0')} - 
                        {Math.floor(vehicle.tw_end / 3600).toString().padStart(2, '0')}:
                        {Math.floor((vehicle.tw_end % 3600) / 60).toString().padStart(2, '0')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          F: {currencySymbol}{vehicle.cost_fixed}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          H: {currencySymbol}{vehicle.cost_per_hour} | K: {currencySymbol}{vehicle.cost_per_km}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewVehicle(vehicle);
                          }}
                          title="View Details"
                        >
                          <ViewIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditVehicle(vehicle);
                          }}
                          title="Edit Vehicle"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVehicle(vehicle);
                          }}
                          title="Delete Vehicle"
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Empty State */}
      {vehicles && vehicles.length === 0 && !vehiclesLoading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <VehicleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Vehicles Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {currentScenario && currentDataset 
              ? `No vehicles found in dataset "${currentDataset.name}" for scenario "${currentScenario.name}". Click "New Vehicle" to add your first vehicle.`
              : 'No vehicles have been created for this project yet. Click "New Vehicle" to add your first vehicle.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenForm}
            sx={{ background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)' }}
          >
            Add First Vehicle
          </Button>
        </Paper>
      )}

      {/* Context Information for empty dataset */}
      {currentScenario && currentDataset && vehicles && vehicles.length === 0 && !vehiclesLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            No vehicles found in <strong>{currentDataset.name}</strong> for scenario <strong>{currentScenario.name}</strong>.
            You can add vehicles using the "New Vehicle" button or import them from a CSV file.
          </Typography>
        </Alert>
      )}

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <VehicleIcon sx={{ mr: 1 }} />
            Vehicle Details: {viewingVehicle?.description}
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingVehicle && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>ID:</strong> {viewingVehicle.id}
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  <strong>Profile:</strong>
                </Typography>
                <Chip 
                  label={viewingVehicle.profile.charAt(0).toUpperCase() + viewingVehicle.profile.slice(1)}
                  color={getVehicleProfileColor(viewingVehicle.profile) as any}
                  size="small"
                />
              </Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Start Location:</strong> {availableLocations?.find(loc => loc.id === viewingVehicle.start_location_id)?.name || 'Location not found'}
              </Typography>
              {viewingVehicle.end_location_id && viewingVehicle.end_location_id !== viewingVehicle.start_location_id && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>End Location:</strong> {availableLocations?.find(loc => loc.id === viewingVehicle.end_location_id)?.name || 'Location not found'}
                </Typography>
              )}
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Capacity:</strong> {viewingVehicle.capacity.join(' / ')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Time Window:</strong> 
                {Math.floor(viewingVehicle.tw_start / 3600).toString().padStart(2, '0')}:
                {Math.floor((viewingVehicle.tw_start % 3600) / 60).toString().padStart(2, '0')} - 
                {Math.floor(viewingVehicle.tw_end / 3600).toString().padStart(2, '0')}:
                {Math.floor((viewingVehicle.tw_end % 3600) / 60).toString().padStart(2, '0')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Fixed Cost:</strong> {currencySymbol}{viewingVehicle.cost_fixed}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Cost per Hour:</strong> {currencySymbol}{viewingVehicle.cost_per_hour}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Cost per Kilometer:</strong> {currencySymbol}{viewingVehicle.cost_per_km}
              </Typography>
              {viewingVehicle.skills && viewingVehicle.skills.length > 0 && (
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Skills:</strong> {viewingVehicle.skills.join(', ')}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button 
            variant="outlined" 
            onClick={() => {
              setViewDialogOpen(false);
              if (viewingVehicle) handleEditVehicle(viewingVehicle);
            }}
            startIcon={<EditIcon />}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Form Dialog - INTEGRATED WITH LOCATION MASTER */}
      <ErrorBoundary>
        <VehicleForm
          open={isFormOpen}
          onClose={handleCloseForm}
          projectId={currentProject?.id || 0}
          vehicle={editingVehicle}
        />
      </ErrorBoundary>

      {/* Bulk Import Dialog */}
      {/* <BulkImportDialog
        open={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onSuccess={handleImportSuccess}
        entityType="vehicles"
        projectId={currentProject?.id || 0}
        availableLocations={availableLocations || []}
      /> */}

      {/* Bulk Export Dialog */}
      {/* <BulkExportDialog
        open={isBulkExportOpen}
        onClose={() => setIsBulkExportOpen(false)}
        entityType="vehicles"
        data={vehicles || []}
        projectName={currentProject?.name || 'Unknown'}
      /> */}
    </ProfessionalLayout>
  );
};
